import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit2, Image as ImgIcon, Eye, EyeOff, Wand2, AlertTriangle, Users } from "lucide-react";
import api from "../../lib/api";
import { TOURNAMENT, CATEGORY_YEARS, categoryLabel } from "../../lib/constants";
import TeamManagersModal from "./TeamManagersModal";

const fileToBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
});

export default function AdminTeams() {
    const [teams, setTeams] = useState([]);
    const [form, setForm] = useState({ name: "", category: TOURNAMENT.categories[0], color: "#7FD93C", motto: "", logo_base64: "" });
    const [editing, setEditing] = useState(null);
    const [showAuto, setShowAuto] = useState(false);
    const [cleaning, setCleaning] = useState(false);
    const [managersTeam, setManagersTeam] = useState(null); // { id, name } | null

    const load = () => api.get("/teams").then(r => setTeams(r.data));
    useEffect(() => { load(); }, []);

    const autoCount = useMemo(() => teams.filter(t => t.auto_created_from_import).length, [teams]);
    const visibleTeams = useMemo(() => showAuto ? teams : teams.filter(t => !t.auto_created_from_import), [teams, showAuto]);

    const save = async (e) => {
        e.preventDefault();
        if (editing) await api.put(`/teams/${editing}`, form);
        else await api.post("/teams", form);
        setForm({ name: "", category: TOURNAMENT.categories[0], color: "#7FD93C", motto: "", logo_base64: "" });
        setEditing(null);
        load();
    };

    const del = async (id) => {
        if (!window.confirm("Supprimer cette équipe et son roster ?")) return;
        await api.delete(`/teams/${id}`); load();
    };

    const onFile = async (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const b = await fileToBase64(f);
        setForm({ ...form, logo_base64: b });
    };

    const cleanupDuplicates = async () => {
        if (!window.confirm(
            `Nettoyer les doublons d'import ?\n\n` +
            `Cette action va :\n` +
            `1) Réassocier les parties de l'horaire aux équipes inscrites (matching par nom normalisé : insensible aux accents, espaces et majuscules)\n` +
            `2) Supprimer les équipes auto-créées par l'import Excel qui ne sont plus référencées par aucune partie/joueur/inscription\n\n` +
            `Aucune équipe inscrite manuellement ne sera supprimée.`
        )) return;
        setCleaning(true);
        try {
            const { data } = await api.post("/games/relink_teams");
            alert(`✓ Nettoyage terminé.\n\n• Parties réassociées : ${data.games_relinked}\n• Doublons supprimés : ${data.auto_teams_deleted}`);
            await load();
        } catch (ex) {
            alert("Erreur : " + (ex?.response?.data?.detail || ex.message));
        } finally {
            setCleaning(false);
        }
    };

    return (
        <div data-testid="admin-teams">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-widest">Équipes</h1>
                <div className="flex flex-wrap items-center gap-2">
                    {autoCount > 0 && (
                        <button
                            onClick={() => setShowAuto(v => !v)}
                            className="btn-ghost flex items-center gap-2"
                            data-testid="toggle-show-auto"
                        >
                            {showAuto ? <EyeOff size={14} /> : <Eye size={14} />}
                            {showAuto ? "Cacher" : "Afficher"} auto-importées ({autoCount})
                        </button>
                    )}
                    {autoCount > 0 && (
                        <button
                            onClick={cleanupDuplicates}
                            disabled={cleaning}
                            className="btn-primary flex items-center gap-2"
                            data-testid="btn-cleanup-duplicates"
                        >
                            <Wand2 size={14} />
                            {cleaning ? "Nettoyage..." : "Nettoyer les doublons"}
                        </button>
                    )}
                </div>
            </div>

            {autoCount > 0 && !showAuto && (
                <div className="bg-amber-500/10 border-l-4 border-amber-400 p-3 mb-5 text-sm flex items-start gap-2" data-testid="auto-banner">
                    <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="font-semibold text-amber-200">
                            {autoCount} équipe{autoCount > 1 ? "s" : ""} auto-importée{autoCount > 1 ? "s" : ""} cachée{autoCount > 1 ? "s" : ""}
                        </div>
                        <div className="text-amber-100/70 text-xs mt-0.5">
                            Ces équipes ont été créées automatiquement par un import Excel. Clique « Nettoyer les doublons » pour réassocier les parties à tes équipes inscrites et supprimer les doublons.
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={save} className="glass-card p-5 grid md:grid-cols-6 gap-3 mb-6 items-end">
                <input required className="input-field" placeholder="Nom" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="team-name" />
                <select className="input-field" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="team-category">
                    {TOURNAMENT.categories.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                </select>
                <input className="input-field" placeholder="Devise" value={form.motto} onChange={e => setForm({ ...form, motto: e.target.value })} />
                <input type="color" className="input-field h-12 cursor-pointer" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
                <label className="btn-ghost cursor-pointer flex items-center gap-2 justify-center"><ImgIcon size={14} />Logo<input type="file" accept="image/*" onChange={onFile} className="hidden" /></label>
                <button type="submit" className="btn-primary justify-center"><Plus size={14} /> {editing ? "Modifier" : "Créer"}</button>
                {form.logo_base64 && <img src={form.logo_base64} alt="" className="h-10 w-10 object-contain col-span-1" />}
            </form>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleTeams.map(t => (
                    <div key={t.id} className={`glass-card p-4 flex items-center gap-3 ${t.auto_created_from_import ? "border-l-2 border-amber-400/50" : ""}`} data-testid={`admin-team-${t.id}`}>
                        {t.logo_base64 ? <img src={t.logo_base64} alt="" className="h-12 w-12 object-contain shrink-0" /> : <div className="h-12 w-12 angle-tag flex items-center justify-center font-display text-lg shrink-0" style={{ background: t.color + "33", color: t.color }}>{t.name.slice(0,2).toUpperCase()}</div>}
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-lg sm:text-xl uppercase truncate">{t.name}</div>
                            <div className="text-xs text-white/50 flex items-center gap-2">
                                {t.category}
                                {t.auto_created_from_import && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-300 text-[10px] uppercase tracking-wider">Auto-Excel</span>}
                            </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                            <button
                                onClick={() => setManagersTeam({ id: t.id, name: t.name })}
                                className="btn-ghost p-2 text-[#4FE0D5] border-[#4FE0D5]/30"
                                title="Gérer les responsables"
                                data-testid={`btn-managers-${t.id}`}
                            >
                                <Users size={12} />
                            </button>
                            <button onClick={() => { setEditing(t.id); setForm({ name: t.name, category: t.category, color: t.color, motto: t.motto || "", logo_base64: t.logo_base64 || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="btn-ghost p-2"><Edit2 size={12} /></button>
                            <button onClick={() => del(t.id)} className="btn-ghost p-2 text-rose-400 border-rose-400/30"><Trash2 size={12} /></button>
                        </div>
                    </div>
                ))}
                {visibleTeams.length === 0 && (
                    <div className="col-span-full text-center py-12 text-white/50">
                        Aucune équipe à afficher.{autoCount > 0 && !showAuto && " Coche « Afficher auto-importées » pour voir les équipes créées par l'import Excel."}
                    </div>
                )}
            </div>

            <TeamManagersModal
                open={!!managersTeam}
                teamId={managersTeam?.id}
                teamName={managersTeam?.name}
                onClose={(_saved) => setManagersTeam(null)}
            />
        </div>
    );
}
